import React from 'react';
import { ArrowRight, Download, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import profileData from '../data/profileData.json';
import { GitHubIcon, LinkedInIcon } from '../components/SocialIcons';

const Home = () => {
  const { personal } = profileData;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <h1 className="text-5xl font-bold text-[#1e3a5f] mb-4">
                {personal.name}
              </h1>
              <h2 className="text-2xl text-[#475569] mb-6">
                {personal.title}
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {personal.summary}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 mb-8">
                <a href="#projects">
                  <Button className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-6 py-3">
                    View Projects
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </a>
                <a
                  href={personal.resumeUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-[#2563eb] text-[#2563eb] hover:bg-blue-50 px-6 py-3">
                    <Download className="mr-2" size={18} />
                    Download Resume
                  </Button>
                </a>
              </div>

              {/* Social Links */}
              <div className="flex space-x-4">
                <a
                  href={personal.social.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-gray-300 rounded-md hover:border-[#2563eb] hover:bg-blue-50 transition-all duration-200"
                  aria-label="GitHub"
                >
                  <GitHubIcon className="h-5 w-5 text-gray-700" />
                </a>
                <a
                  href={personal.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-gray-300 rounded-md hover:border-[#2563eb] hover:bg-blue-50 transition-all duration-200"
                  aria-label="LinkedIn"
                >
                  <LinkedInIcon className="h-5 w-5 text-gray-700" />
                </a>
                <a
                  href={personal.social.dockerhub}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-gray-300 rounded-md hover:border-[#2563eb] hover:bg-blue-50 transition-all duration-200"
                  aria-label="DockerHub"
                >
                  <Globe size={20} className="text-gray-700" />
                </a>
              </div>
            </div>

            {/* Right Content - Photo */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-[#2563eb] rounded-lg transform translate-x-4 translate-y-4"></div>
                <img
                  src={personal.photo}
                  alt={personal.name}
                  className="relative w-80 h-80 object-cover rounded-lg shadow-xl border-4 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2563eb] mb-2">10+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2563eb] mb-2">10+</div>
              <div className="text-gray-600">Major Projects</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2563eb] mb-2">6</div>
              <div className="text-gray-600">Companies</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2563eb] mb-2">4+</div>
              <div className="text-gray-600">Certifications</div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] text-center mb-12">
            Key Highlights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">
                AWS Certified Professional
              </h3>
              <p className="text-gray-600 leading-relaxed">
                AWS Certified Solutions Architect – Professional with extensive experience in cloud architecture and serverless computing.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">
                Microservices Expert
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Specialized in designing and implementing scalable microservices architecture using Spring Cloud and event-driven patterns.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">
                Technical Leadership
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Proven track record of leading cross-functional teams and delivering complex enterprise solutions for Fortune 500 clients.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;